import React from "react";
import { AlertCircle } from "lucide-react";
import cn from "./cn";

/**
 * FormField · Label · HelpText · FieldError — the wrapper around one control.
 *
 *   <FormField label="Course Name" error={errors.name} required>
 *     <Input value={name} onChange={…} placeholder="Enter course name" />
 *   </FormField>
 *
 * `FormField` generates the control's id and clones its child to inject `id`,
 * `aria-describedby` and `aria-invalid`. Those are real DOM attributes, so it
 * works on a raw `<input>`, on `Input`/`NativeSelect`/`Textarea`, and on a
 * react-bootstrap `<Form.Control>` alike.
 *
 * It does not style the control. The field owns the label, the ids and the
 * messages; the control owns how it looks.
 *
 * Props:
 *   label      — visible label text (omit for an unlabelled field)
 *   htmlFor    — bind to an existing id instead of generating one. Injection is
 *                skipped when this is passed: the caller owns the wiring.
 *   required   — appends the required mark and sets `required` on the control
 *   error      — string or node; renders FieldError and marks the control invalid
 *   hint       — help text under the control, hidden while an error shows
 *   labelTone  — "default" | "muted"
 *   children   — the control
 */
const FormField = ({
  label,
  htmlFor,
  required = false,
  error,
  hint,
  labelTone = "default",
  className = "",
  labelClassName = "",
  children,
}) => {
  const generatedId = React.useId();
  const controlId = htmlFor || `field-${generatedId}`;

  // Only reference ids that actually render, or a screen reader is pointed at an
  // element that is not in the tree.
  const showHint = Boolean(hint) && !error;
  const errorId = error ? `${controlId}-error` : null;
  const hintId = showHint ? `${controlId}-hint` : null;
  const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;

  const control =
    React.isValidElement(children) && !htmlFor
      ? React.cloneElement(children, {
          id: children.props.id || controlId,
          "aria-describedby": children.props["aria-describedby"] || describedBy,
          "aria-invalid": children.props["aria-invalid"] ?? (error ? true : undefined),
          required: children.props.required ?? (required || undefined),
        })
      : children;

  return (
    <div className={cn("tw-flex tw-min-w-0 tw-flex-col tw-gap-1.5", className)}>
      {label && (
        <Label
          htmlFor={controlId}
          required={required}
          tone={labelTone}
          className={labelClassName}
        >
          {label}
        </Label>
      )}
      {control}
      {showHint && <HelpText id={hintId}>{hint}</HelpText>}
      {error && <FieldError id={errorId}>{error}</FieldError>}
    </div>
  );
};

/**
 * Label — a field label.
 *
 * `default` is body ink, for a form. `muted` lets the values read louder than
 * the field names, which is what a filter drawer wants.
 */
const LABEL_TONE_CLASSES = {
  default: "tw-text-content",
  muted: "tw-text-content-muted",
};

const Label = ({
  htmlFor,
  required = false,
  tone = "default",
  className = "",
  children,
  ...props
}) => (
  <label
    htmlFor={htmlFor}
    className={cn(
      "tw-text-[0.8125rem] tw-font-semibold",
      LABEL_TONE_CLASSES[tone] || LABEL_TONE_CLASSES.default,
      className
    )}
    {...props}
  >
    {children}
    {required && (
      // aria-hidden because `required` is already set on the control itself; a
      // screen reader announcing "required" twice per field is noise.
      <span className="tw-ml-0.5 tw-text-danger" aria-hidden="true">
        *
      </span>
    )}
  </label>
);

/** HelpText — the quiet line under a control. */
const HelpText = ({ id, className = "", children }) => (
  <span id={id} className={cn("tw-text-xs tw-leading-snug tw-text-content-subtle", className)}>
    {children}
  </span>
);

/**
 * FieldError — the loud one.
 *
 * `role="alert"` so a message that appears after submit is announced rather than
 * sitting there silently, and an icon so the state is not carried by colour
 * alone (WCAG 1.4.1).
 */
const FieldError = ({ id, className = "", children }) => (
  <span
    id={id}
    role="alert"
    className={cn(
      "tw-flex tw-items-start tw-gap-1 tw-text-xs tw-leading-snug tw-text-danger",
      className
    )}
  >
    <AlertCircle size={13} className="tw-mt-px tw-shrink-0" aria-hidden="true" />
    <span>{children}</span>
  </span>
);

export { FormField, Label, HelpText, FieldError };
export default FormField;
